import { useContracts, useProvider } from './useContract'
import { ABI_LAUNCHPAD_POOL } from '../utils/abis'
import { useWeb3React } from '@web3-react/core'
import { useEffect, useState } from 'react'
import { useNow } from './useCountDown'
import { BigNumber, constants } from 'ethers'
import { isEqualString } from '../utils'

interface ReleaseInfo {
  lastTime: number
  INT_BASE: number
  firstRate: number
  remainRate: number
  timeStep: number
  firstTime: number
  onShareAmount: BigNumber
  linearReleaseTime: number
}

export interface ReleaseSection {
  time: number
  amount: BigNumber
  claimed: boolean
  canClaim: boolean
}

export function useClaimSection(contractAddress: string | undefined) {
  const { multiCallContract, contract } = useContracts(contractAddress, ABI_LAUNCHPAD_POOL)
  const provider = useProvider()
  const { account } = useWeb3React()

  const [releaseInfo, setReleaseInfo] = useState<ReleaseInfo | null>(null)
  const { now } = useNow()
  const [sections, setSections] = useState<ReleaseSection[]>([])
  const [canClaimedAmount, setCanClaimedAmount] = useState<BigNumber>(constants.Zero)
  const [claimedAmount, setClaimedAmount] = useState<BigNumber>(constants.Zero)
  useEffect(() => {
    const loadData = () => {
      if (account && multiCallContract && provider) {
        provider
          .all([
            multiCallContract._unlockTimeRecords(account),
            multiCallContract.INT_BASE(), //
            multiCallContract._firstRate(), // 第一期解锁比例
            multiCallContract._remainStepRate(), // 后面每期解锁比例
            multiCallContract._timeStep(), // 后面每期间隔时间
            multiCallContract._firstReleaseTime(), // 第一期释放时间
            multiCallContract._oneShareAmount(), //
            multiCallContract._linearReleaseTime(), //
          ])
          .then((res) => {
            setReleaseInfo({
              lastTime: +res[0],
              INT_BASE: +res[1],
              firstRate: +res[2],
              remainRate: +res[3],
              timeStep: +res[4],
              firstTime: +res[5],
              onShareAmount: res[6],
              linearReleaseTime: +res[7],
            })
          })
      }
    }
    loadData()
    if (contract) {
      const onReleaseListener = (address) => {
        if (isEqualString(address, account)) {
          loadData()
        }
      }
      contract.on('Release', onReleaseListener)
      return () => {
        contract.removeListener('Release', onReleaseListener)
      }
    }
  }, [multiCallContract, provider, account, contract])
  useEffect(() => {
    if (releaseInfo) {
      const steps = Math.floor((releaseInfo.INT_BASE - releaseInfo.firstRate) / releaseInfo.remainRate)
      const sections: ReleaseSection[] = []
      let totalRate = constants.Zero
      for (let i = 0; i <= steps; i++) {
        if (i === 0) {
          sections.push({
            time: releaseInfo.firstTime,
            amount: releaseInfo.onShareAmount.mul(releaseInfo.firstRate).div(releaseInfo.INT_BASE),
            claimed: releaseInfo.lastTime >= releaseInfo.firstTime,
            canClaim: now > releaseInfo.firstTime,
          })
          totalRate = totalRate.add(releaseInfo.firstRate)
        } else {
          sections.push({
            time: releaseInfo.linearReleaseTime + i * releaseInfo.timeStep,
            amount: releaseInfo.onShareAmount.mul(releaseInfo.remainRate).div(releaseInfo.INT_BASE),
            claimed: releaseInfo.lastTime >= releaseInfo.linearReleaseTime + i * releaseInfo.timeStep,
            canClaim: now > releaseInfo.linearReleaseTime + i * releaseInfo.timeStep,
          })
          totalRate = totalRate.add(releaseInfo.remainRate)
        }
      }
      if (totalRate.lt(releaseInfo.INT_BASE)) {
        sections.push({
          time: releaseInfo.linearReleaseTime + (steps + 1) * releaseInfo.timeStep,
          amount: releaseInfo.onShareAmount.mul(releaseInfo.INT_BASE - +totalRate).div(releaseInfo.INT_BASE),
          claimed: releaseInfo.lastTime >= releaseInfo.linearReleaseTime + (steps + 1) * releaseInfo.timeStep,
          canClaim: now > releaseInfo.linearReleaseTime + (steps + 1) * releaseInfo.timeStep,
        })
      }
      setCanClaimedAmount(
        sections.filter((x) => x.canClaim && !x.claimed).reduce((x, y) => x.add(y.amount), constants.Zero),
      )
      setClaimedAmount(sections.filter((x) => x.claimed).reduce((x, y) => x.add(y.amount), constants.Zero))
      setSections(sections)
    }
  }, [now, releaseInfo])
  return { sections, canClaimedAmount, claimedAmount, releaseInfo }
}
